import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Paperclip, Send } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import { Carte } from './ConvCartes';
import { lireCarte } from '../../utils/lireCarte';
import './agent-conv.css';
import { VisageExpert } from '../ig/VisageExpert';

const API = getApiUrl();
const AGENTS = { manu: { nom: 'Manu', lettre: 'M', couleur: '#7B6FD6' }, leo: { nom: 'Léo', lettre: 'L', couleur: '#E6B93A' }, clara: { nom: 'Clara', lettre: 'C', couleur: '#E2582C' }, sophie: { nom: 'Sophie', lettre: 'S', couleur: '#4D7C2B' }, theo: { nom: 'Théo', lettre: 'T', couleur: '#3B6FB6' } };
const ACCEPT = { photo: 'image/*', fichier: '.xlsx,.xls,.csv,.pdf,image/*' };
const CHEMIN = { manu: 'action', leo: 'leo' };
const MISSIONS = ['recrutement', 'mois', 'catalogue'];

const Bulle = ({ m, onAction, actif }) => (
  <div className={`cv-msg is-${m.de}`} data-testid={`conv-msg-${m.id}`}>
    {(m.texte || m.piece) && <div className="cv-bulle">{m.texte}{m.piece && <span className="cv-piece" data-testid="conv-piece">{m.piece.nom}</span>}</div>}
    {m.carte && <Carte c={m.carte} onAction={onAction} actif={actif} />}
    {m.note && <p className="cv-note">{m.note}</p>}
  </div>
);

const Rapides = ({ items, onTap }) => (
  <div className="cv-rapides" data-testid="conv-rapides">
    {items.map((r, i) => <button key={`${r.k}-${i}`} type="button" className="cv-rapide" onClick={() => onTap(r)} data-testid={`conv-rapide-${r.k}${r.v?.option ? `-${r.v.option}` : ''}`}>{r.label}</button>)}
  </div>
);

/* LOT 11 · un seul composant de conversation pour tous les agents et les missions */
export const AgentConversation = ({ convId, agent: agentProp = 'manu', onClose, onNav }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState('');
  const fileRef = useRef(null);
  const bas = useRef(null);
  const agent = c?.agent || agentProp;
  const mission = MISSIONS.includes(c?.sujet);
  const ag = AGENTS[agent] || AGENTS.manu;
  const charger = () => axios.get(`${API}/agent-conv/${convId}`, { withCredentials: true }).then((r) => { setC(r.data); setErreur(''); }).catch(() => setErreur('Je n’ai pas pu ouvrir la conversation.'));
  useEffect(() => { charger(); }, [convId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { bas.current?.scrollIntoView({ block: 'end' }); }, [c?.messages?.length, busy]);

  const appel = async (fn) => {
    setBusy(true); setErreur('');
    try { const r = await fn(); setC(r.data); return r.data; } catch (e) { setErreur(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Pas de réseau ou erreur : votre conversation est gardée.'); return null; } finally { setBusy(false); }
  };
  const action = (k, v) => {
    if (k === 'nav') return onNav?.(v.to);
    if (k === 'photo' || k === 'fichier') { fileRef.current.accept = ACCEPT[k]; return fileRef.current.click(); }
    return appel(() => axios.post(`${API}/agent-conv/${convId}/${mission ? 'mission' : (CHEMIN[agent] || 'action')}`, { k, v }, { withCredentials: true }));
  };
  const [lecture, setLecture] = useState('');
  const envoyerCarte = async (f) => {
    setBusy(true); setErreur('');
    try { const plats = await lireCarte(f, setLecture); const r = await axios.post(`${API}/agent-conv/${convId}/carte`, { plats, nom: f.name }, { withCredentials: true }); setC(r.data); } catch (e) { setErreur(e?.detail || e?.response?.data?.detail || 'Lecture impossible : votre conversation est gardée.'); } finally { setBusy(false); setLecture(''); fileRef.current.value = ''; }
  };
  const envoyerFichier = (f) => { if (!f) return; if (agent === 'leo' && !mission) return envoyerCarte(f); const fd = new FormData(); fd.append('file', f); appel(() => axios.post(`${API}/agent-conv/${convId}/${mission ? 'mission-fichier' : 'fichier'}`, fd, { withCredentials: true })); fileRef.current.value = ''; };
  const envoyer = () => { const t = texte.trim(); if (!t) return; setTexte(''); appel(() => axios.post(`${API}/agent-conv/${convId}/${mission ? 'mission-message' : 'message'}`, { texte: t }, { withCredentials: true })); };
  const reglages = (r) => appel(() => axios.put(`${API}/agent-conv/${convId}/reglages`, { reglages: r }, { withCredentials: true }));
  const valider = async () => {
    const d = await appel(() => axios.post(`${API}/agent-conv/${convId}/valider`, {}, { withCredentials: true }));
    if (d?.statut === 'validee') toast.success('Planning publié', { duration: 10000, action: { label: 'Annuler', onClick: () => appel(() => axios.post(`${API}/agent-conv/${convId}/annuler-validation`, {}, { withCredentials: true })) } });
  };
  const onCarte = (k, v) => (k === 'reglages:save' ? reglages(v) : action(k, v));

  const msgs = c?.messages || [];
  const dernier = msgs[msgs.length - 1];
  const finPatron = msgs.map((m) => m.de).lastIndexOf('patron');
  const rapides = msgs.slice(finPatron + 1).flatMap((m) => m.rapides || []);
  const pret = c && c.statut !== 'validee' && dernier?.carte?.type === 'planning';
  const pied = c && c.statut !== 'validee' && dernier?.carte?.type === 'pied' ? dernier.carte : null;

  return createPortal(
    <div className="cv-ecran" role="dialog" aria-label={`${ag.nom} · conversation`} data-testid="agent-conversation">
      <div className="cv-top">
        <button type="button" className="cv-retour" onClick={onClose} aria-label="Retour" data-testid="conv-retour" data-keepicon=""><ChevronLeft /></button>
        <span className="cv-pastille" style={{ background: ag.couleur, overflow: 'hidden' }} data-testid="conv-visage"><VisageExpert agent={AGENTS[agent] ? agent : 'manu'} nom={ag.nom} lettre={ag.lettre} taille={40} /></span>
        <div className="cv-titres"><b data-testid="conv-titre">{c?.titre || `${ag.nom} · …`}</b><small>{c?.sous_titre}</small></div>
      </div>
      <div className="cv-fil" data-testid="conv-fil">
        {!c && !erreur && <p className="cv-note">Chargement…</p>}
        {msgs.map((m, i) => <Bulle key={m.id} m={m} onAction={onCarte} actif={i > finPatron && !busy} />)}
        {busy && <div className="cv-msg is-agent"><div className="cv-bulle cv-tape" data-testid="conv-ecrit">{lecture || `${ag.nom} lit et calcule…`}</div></div>}
        {!busy && rapides.length > 0 && <Rapides items={rapides} onTap={(r) => action(r.k, r.v)} />}
        {erreur && <div className="cv-erreur" role="alert" data-testid="conv-erreur">{erreur}<button type="button" onClick={charger} data-testid="conv-reessayer">Réessayer</button></div>}
        <div ref={bas} />
      </div>
      <input ref={fileRef} type="file" hidden onChange={(e) => envoyerFichier(e.target.files?.[0])} data-testid="conv-file-input" />
      {pied ? (
        <div className="cv-bas is-action">
          <button type="button" className="cv-btn" onClick={() => action(pied.secondaire.k)} disabled={busy} data-testid="conv-pied-secondaire">{pied.secondaire.label}</button>
          <button type="button" className={`cv-btn ${/★/.test(pied.principal.label) ? 'is-cout' : 'is-main'}`} onClick={() => action(pied.principal.k)} disabled={busy} data-testid="conv-pied-principal">{pied.principal.label}</button>
        </div>
      ) : pret ? (
        <div className="cv-bas is-action">
          <button type="button" className="cv-btn" onClick={() => action('modifier')} disabled={busy} data-testid="conv-modifier">Modifier</button>
          <button type="button" className="cv-btn is-noir" onClick={valider} disabled={busy} data-testid="conv-valider">Publier · 10 ★</button>
        </div>
      ) : (
        <div className="cv-bas">
          <button type="button" className="cv-rond" onClick={() => action('fichier')} disabled={busy || c?.statut === 'validee'} aria-label="Joindre une photo ou un fichier" data-testid="conv-joindre" data-keepicon=""><Paperclip /></button>
          <input className="cv-champ" value={texte} onChange={(e) => setTexte(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && envoyer()} placeholder={`Écrivez à ${ag.nom}…`} disabled={busy} data-testid="conv-champ" />
          <button type="button" className="cv-rond is-noir" onClick={envoyer} disabled={busy || !texte.trim()} aria-label="Envoyer" data-testid="conv-envoyer" data-keepicon=""><Send /></button>
        </div>
      )}
    </div>,
    document.body,
  );
};
